/**
 * View-model de la card de mesa (grilla): estado derivado + meta + badges.
 * Una sola verdad visual entre grilla y proto mesas-v2.
 */
import type { TableDraftSummary } from './hospitalityTypes';
import { resolveMesaVisualState, type MesaVisualState } from './mesaVisualStatus';

export type MesaCardBadge = {
  kind: 'kitchen' | 'ready' | 'signal' | 'unpaid' | 'liberate' | 'payqr';
  label: string;
  pulse?: boolean;
};

export type MesaCardView = {
  state: MesaVisualState;
  /** Línea descriptiva (cubiertos·mozo, reserva, draft, etc.). */
  meta: string;
  /** CTA inline opcional ("Abrir mesa" en libre). */
  hint: string | null;
  badges: MesaCardBadge[];
};

export function mesaCardViewModel(args: {
  capacity: number;
  tableStatus: string;
  occupiedAt?: string | null;
  waiterName: string | null;
  hasOpenSession: boolean;
  sessionCovers: number | null;
  sessionPaymentStatus: string | null;
  draft: TableDraftSummary | null;
  kitchen: { open: number; ready: number; unpaid: boolean };
  /** Reserva activa próxima por mesa (snapshot F9): hora legible + vencida. */
  reservation?: { timeLabel: string | null; overdue: boolean } | null;
}): MesaCardView {
  const draftCount = args.draft?.draftLineCount ?? 0;
  const lookingCount = args.draft?.lookingGuestCount ?? 0;
  const state = resolveMesaVisualState({
    tableStatus: args.tableStatus,
    occupiedAt: args.occupiedAt,
    hasOpenSession: args.hasOpenSession,
    draftCount,
    lookingCount,
    needsLiberate: false, // el snapshot no expone needs_liberate (ver Ficha/RPC)
    paidVisit: args.sessionPaymentStatus === 'paid',
    partialPaid: args.sessionPaymentStatus === 'partial',
    hasActiveReservation: args.tableStatus === 'reserved' || Boolean(args.reservation),
    reservationOverdue: args.reservation?.overdue ?? false,
  });

  const badges: MesaCardBadge[] = [];
  if (args.kitchen.open > 0) badges.push({ kind: 'kitchen', label: `${args.kitchen.open} en cocina` });
  if (args.kitchen.ready > 0) badges.push({ kind: 'ready', label: `${args.kitchen.ready} listo` });
  if (args.draft?.hasCallWaiter) badges.push({ kind: 'signal', label: 'Te llaman', pulse: true });
  if (args.draft?.hasReadyToOrder) badges.push({ kind: 'signal', label: 'Ya pidieron', pulse: true });
  if (args.draft?.hasRequestBill) badges.push({ kind: 'signal', label: 'Piden la cuenta', pulse: true });
  if (args.kitchen.unpaid) badges.push({ kind: 'unpaid', label: 'sin cobrar' });
  if (args.sessionPaymentStatus === 'pending') badges.push({ kind: 'payqr', label: 'pago QR' });
  if (state === 'paid') badges.push({ kind: 'liberate', label: 'Liberar', pulse: true });

  const covers = args.sessionCovers ?? args.capacity;
  const waiter = args.waiterName;
  let meta: string;
  let hint: string | null = null;
  switch (state) {
    case 'occupied':
    case 'round':
    case 'paid':
      meta = `${covers} cubiertos · ${waiter ?? 'sin mozo'}`;
      break;
    case 'drafting':
      meta = 'Draft compartido · QR';
      break;
    case 'looking':
      meta = `${lookingCount > 0 ? lookingCount : '—'} mirando la carta`;
      break;
    case 'reserved':
    case 'overdue':
      meta = args.reservation?.timeLabel
        ? `${state === 'overdue' ? 'Reserva pasada' : 'Reserva'} ${args.reservation.timeLabel}`
        : 'Mesa reservada';
      break;
    case 'closed':
      meta = 'Fuera de servicio';
      break;
    default:
      meta = `${args.capacity} lugares`;
      hint = 'Abrir mesa';
  }

  return { state, meta, hint, badges };
}

/** Filtros de grilla/KPI del proto v2. */
export type MesaCardFilter =
  | 'all' | 'free' | 'occupied' | 'paid' | 'reserved'
  | 'signals' | 'looking' | 'kitchen' | 'unpaid';

export function mesaCardMatchesFilter(view: MesaCardView, filter: MesaCardFilter): boolean {
  switch (filter) {
    case 'all': return true;
    case 'free': return view.state === 'free';
    case 'occupied': return view.state === 'occupied' || view.state === 'round';
    case 'paid': return view.state === 'paid';
    case 'reserved': return view.state === 'reserved' || view.state === 'overdue';
    case 'looking': return view.state === 'looking';
    case 'signals': return view.badges.some((b) => b.kind === 'signal');
    case 'kitchen': return view.badges.some((b) => b.kind === 'kitchen');
    case 'unpaid': return view.badges.some((b) => b.kind === 'unpaid' || b.kind === 'payqr');
  }
}
